import React from "react";
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormGroup,
  Checkbox,
  Button,
  Grid,
  alpha,
  Divider,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Send as SendIcon } from "@mui/icons-material";
import PropTypes from "prop-types";

const QuestionsStep = ({
  questions = [],
  answers,
  handleAnswerChange,
  handleCheckboxChange,
  handleBack,
  handleSubmit,
  primaryColor,
  bgColor,
}) => {
  // Sort questions by orderIndex to ensure correct display order
  const sortedQuestions = [...questions].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {sortedQuestions.map((q, index) => {
          // Parse options if they exist (split by semicolon)
          const optionsList = q.options ? q.options.split(";") : [];

          return (
            <Grid item xs={12} key={q.id}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  backgroundColor: "white",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: primaryColor,
                    boxShadow: `0 4px 20px ${alpha(primaryColor, 0.08)}`,
                  },
                  // Visual indicator for answered vs unanswered
                  borderLeft:
                    answers[q.id] && answers[q.id].length > 0
                      ? `4px solid ${primaryColor}`
                      : "1px solid #e0e0e0",
                }}
              >
                {/* Question Text */}
                <Typography variant="h6" fontSize="1rem" gutterBottom fontWeight="medium">
                  {index + 1}. {q.questionText}{" "}
                  {q.isRequired && (
                    <Typography component="span" color="error" fontWeight="bold">
                      *
                    </Typography>
                  )}
                </Typography>

                {/* --- INPUT TYPE SWITCH --- */}
                <Box mt={2}>
                  {/* 1. SHORT TEXT */}
                  {q.fieldType === "SHORT_TEXT" && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Your answer"
                      value={answers[q.id] || ""}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      required={q.isRequired}
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: primaryColor,
                        },
                      }}
                    />
                  )}

                  {/* 2. LONG TEXT */}
                  {q.fieldType === "LONG_TEXT" && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      placeholder="Type your answer here..."
                      value={answers[q.id] || ""}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      required={q.isRequired}
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: primaryColor,
                        },
                      }}
                    />
                  )}

                  {/* 3. RADIO BUTTONS (Single Choice) */}
                  {q.fieldType === "RADIO" && (
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={answers[q.id] || ""}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      >
                        <Grid container spacing={1}>
                          {optionsList.map((opt, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                              <FormControlLabel
                                value={opt}
                                control={
                                  <Radio
                                    sx={{
                                      color: "grey.400",
                                      "&.Mui-checked": { color: primaryColor },
                                    }}
                                  />
                                }
                                label={opt}
                                sx={{
                                  width: "100%",
                                  m: 0,
                                  p: 1,
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: answers[q.id] === opt ? primaryColor : "transparent",
                                  bgcolor:
                                    answers[q.id] === opt
                                      ? alpha(primaryColor, 0.05)
                                      : "transparent",
                                  transition: "all 0.2s",
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  )}

                  {/* 4. CHECKBOXES (Multiple Choice) */}
                  {q.fieldType === "CHECKBOX" && (
                    <FormControl component="fieldset" variant="standard">
                      <FormGroup>
                        <Grid container spacing={1}>
                          {optionsList.map((opt, i) => {
                            const isChecked = (answers[q.id] || []).includes(opt);
                            return (
                              <Grid item xs={12} sm={6} md={4} key={i}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => handleCheckboxChange(q.id, opt)}
                                      name={opt}
                                      sx={{
                                        color: "grey.400",
                                        "&.Mui-checked": { color: primaryColor },
                                      }}
                                    />
                                  }
                                  label={opt}
                                  sx={{
                                    width: "100%",
                                    m: 0,
                                    p: 1,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: isChecked ? primaryColor : "transparent",
                                    bgcolor: isChecked ? alpha(primaryColor, 0.05) : "transparent",
                                    transition: "all 0.2s",
                                  }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </FormGroup>
                    </FormControl>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* FOOTER ACTIONS */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          size="large"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: "text.secondary",
            borderColor: "grey.300",
            px: 4,
            py: 1,
            borderRadius: 2,
            "&:hover": { borderColor: "grey.500", bgcolor: "grey.50" },
          }}
        >
          Back
        </Button>

        <Button
          type="submit"
          variant="contained"
          size="large"
          color="inherit"
          endIcon={<SendIcon />}
          sx={{
            color: bgColor,
            bgcolor: primaryColor,
            px: 5,
            py: 1.2,
            borderRadius: 2,
            boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
            fontWeight: "bold",
            "&:hover": {
              bgcolor: primaryColor,
              filter: "brightness(0.9)",
            },
          }}
        >
          Submit Application
        </Button>
      </Box>
    </form>
  );
};

QuestionsStep.propTypes = {
  questions: PropTypes.array,
  answers: PropTypes.array,
  handleAnswerChange: PropTypes.func,
  handleCheckboxChange: PropTypes.func,
  handleBack: PropTypes.func,
  handleSubmit: PropTypes.func,
  primaryColor: PropTypes.string,
  bgColor: PropTypes.string,
};

export default QuestionsStep;
